import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import css from './Header.css';
import Datepicker from '../../../components/widgets/Datepicker';
import { AutoCompleteField } from '../../../components/widgets';
import { STATUSES_COLOR_MAP } from '../tableUtils';
import { AUTOCOMPLETE_EVENT_SELECT } from './consts';
import ReportingActionsButton from './components/ReportingActionsButton';
import Tooltip from '../../../components/molecules/Tooltip';
import SubPageHeader from '../../../components/molecules/SubPageHeader';

const translatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING';

// Reusable component for label-value pairs
const LabelValuePair = ({ label, children, className = '', containerClassName = '' }) => (
    <div className={`${css.miniTitleContainer} ${containerClassName}`}>
        <div className={`${css.title} ${className}`}>
            <span>{label}</span>
            {children}
        </div>
    </div>
);

LabelValuePair.propTypes = {
    label: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
};

// Status indicator component
const StatusIndicator = ({ status }) => (
    <div className={css.status}>
        <span className={css.booleanIcon} style={{ backgroundColor: STATUSES_COLOR_MAP[status] }} />
        {status}
    </div>
);

StatusIndicator.propTypes = {
    status: PropTypes.string.isRequired,
};

// App site info component
const AppSiteInfo = ({ appsite }) => (
    <div className={css.appsiteContainer}>
        <img className={css.icon} src={appsite?.icon} alt="" />
        <span className={css.appsiteName}>{appsite?.label}</span>
    </div>
);

AppSiteInfo.propTypes = {
    appsite: PropTypes.shape({
        icon: PropTypes.string,
        label: PropTypes.string,
    }),
};

const Header = ({
    translate,
    testData,
    selectedEvent,
    handleEventChange,
    handleDateChange,
    selectedStartDate,
    selectedEndDate,
    onDeleteClick,
    onEditClick,
    onClose,
}) => {
    const today = new Date();
    const calculatedMaxDate = testData?.endDate
        ? Math.min(new Date(testData.endDate).getTime(), today.getTime())
        : today;

    // Redux selector for sidenav state
    const isSidenavSlim = useSelector(state => state?.settings?.isSidenavSlim ?? false);

    const descriptionLabelRef = useRef(null);
    const hiddenDescriptionRef = useRef(null);
    const subtitleRef = useRef(null);
    const [truncatedDescription, setTruncatedDescription] = useState(testData?.description || '');
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

    const calculateAvailableSpace = useCallback(() => {
        if (
            !subtitleRef.current ||
            !descriptionLabelRef.current ||
            !hiddenDescriptionRef.current ||
            !testData?.description
        )
            return;

        // Calculate available space by subtracting descriptionLabelRef.right from subtitleRef.right
        const availableSpace =
            subtitleRef.current.getBoundingClientRect().right -
            descriptionLabelRef.current.getBoundingClientRect().right -
            32;

        hiddenDescriptionRef.current.textContent = testData.description;
        const fullDescriptionRect = hiddenDescriptionRef.current.getBoundingClientRect();

        if (fullDescriptionRect.width <= availableSpace) {
            setTruncatedDescription(testData.description);
            setShouldShowTooltip(false);
        } else {
            const truncated = `${testData.description.substring(
                0,
                Math.floor(testData.description.length * (availableSpace / fullDescriptionRect.width))
            )}...`;
            setTruncatedDescription(truncated);
            setShouldShowTooltip(true);
        }
    }, [testData?.description]);

    useEffect(() => {
        calculateAvailableSpace();
    }, [calculateAvailableSpace]);

    useEffect(() => {
        const handleResize = () => {
            calculateAvailableSpace();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateAvailableSpace]);

    // Recalculate when sidenav state changes
    useEffect(() => {
        // Add a small delay to allow the sidenav animation to complete
        const timeoutId = setTimeout(() => {
            calculateAvailableSpace();
        }, 300); // Sidenav animation duration

        return () => clearTimeout(timeoutId);
    }, [isSidenavSlim, calculateAvailableSpace]);

    if (!testData) return null;
    return (
        <div className={css.headerContainer}>
            <div className={css.pageHeader}>
                <div className={css.left}>
                    <SubPageHeader
                        className={css.subPageHeader}
                        onGoBack={onClose}
                        labelTranslationKey={`${translatePrefix}.TEST_RESULTS`}
                    />
                    <div className={css.titleDivider}> |</div>
                    <div className={css.testName}>{testData?.testName}</div>
                    <AppSiteInfo appsite={testData?.appsite} />
                </div>
                <ReportingActionsButton onEditClick={onEditClick} onDeleteClick={onDeleteClick} testData={testData} />
            </div>
            <div className={css.subtitle} ref={subtitleRef}>
                <Datepicker
                    onApply={handleDateChange}
                    buttonContainerClass={css.dateContainer}
                    autoApply
                    maxDate={calculatedMaxDate}
                    minDate={testData.startDate}
                    buttonType="button"
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                />
                <AutoCompleteField
                    dataTestId={AUTOCOMPLETE_EVENT_SELECT}
                    isMulti={false}
                    value={selectedEvent}
                    options={testData.events}
                    innerContainerClass={css.eventsContainer}
                    controlled
                    placeholder={`${translatePrefix}.EVENT_NAME`}
                    onChange={handleEventChange}
                />
                <StatusIndicator status={testData.status} />
                <LabelValuePair label={<Translate id={`${translatePrefix}.TEST_PERIOD`} />}>
                    <span className={css.content}>
                        {` ${testData?.startDate || ''} - ${testData?.endDate ||
                            translate('STATIC.BUTTONS.DATEPICKER.TODAY')}`}
                    </span>
                </LabelValuePair>
                {testData?.description && (
                    <LabelValuePair
                        label={<Translate id={`${translatePrefix}.DESCRIPTION`} />}
                        containerClassName={css.descriptionContainer}
                    >
                        <span ref={descriptionLabelRef} />
                        <Tooltip
                            title={shouldShowTooltip ? testData.description : ''}
                            disabled={!shouldShowTooltip}
                            translate={translate}
                        >
                            <span className={css.content}>{` ${truncatedDescription}`}</span>
                        </Tooltip>
                        {/* Hidden element to measure description width */}
                        <span ref={hiddenDescriptionRef} className={`${css.content} ${css.hiddenMeasurement}`} />
                    </LabelValuePair>
                )}
            </div>
        </div>
    );
};

Header.propTypes = {
    testData: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        description: PropTypes.string,
        testName: PropTypes.string,
        status: PropTypes.string,
        appsite: PropTypes.shape({
            icon: PropTypes.string,
            label: PropTypes.string,
        }),
        events: PropTypes.arrayOf(PropTypes.string),
    }),
    handleDateChange: PropTypes.func,
    handleEventChange: PropTypes.func,
    selectedStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    selectedEvent: PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
    selectedEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.number]),
    translate: PropTypes.func,
    onEditClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    onClose: PropTypes.func,
};

export default withLocalize(Header);
